import { Toast } from 'primereact/toast';
import { createContext, ReactNode, useContext, useRef } from 'react';

interface ToastContextType {
  showSuccess: (message: string, summary?: string) => void;
  showError: (message: string, summary?: string) => void;
  showInfo: (message: string, summary?: string) => void;
  showWarning: (message: string, summary?: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider = ({ children }: ToastProviderProps) => {
  const toast = useRef<Toast>(null);

  const showSuccess = (message: string, summary: string = 'Success') => {
    toast.current?.show({
      severity: 'success',
      summary,
      detail: message,
      life: 3000,
    });
  };

  const showError = (message: string, summary: string = 'Error') => {
    toast.current?.show({
      severity: 'error',
      summary,
      detail: message,
      life: 5000,
    });
  };

  const showInfo = (message: string, summary: string = 'Info') => {
    toast.current?.show({
      severity: 'info',
      summary,
      detail: message,
      life: 3000,
    });
  };

  const showWarning = (message: string, summary: string = 'Warning') => {
    toast.current?.show({
      severity: 'warn',
      summary,
      detail: message,
      life: 4000,
    });
  };

  const value: ToastContextType = {
    showSuccess,
    showError,
    showInfo,
    showWarning,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Toast ref={toast} position="bottom-center" />
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
